import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function VendorMappingsCard() {
    const { data, mutate } = useSWR("/divers/mappings", fetcher);
    const items = data?.items || [];
    const [pattern, setPattern] = useState("");
    const [vendor, setVendor] = useState("");
    const [saving, setSaving] = useState(false);

    const add = async () => {
        if (!pattern.trim() || !vendor.trim()) {
            toast.error("Renseigne le motif et le nom du fournisseur");
            return;
        }
        setSaving(true);
        try {
            await api.post("/divers/mappings", { pattern, vendor });
            toast.success("Mapping ajouté");
            setPattern("");
            setVendor("");
            mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        await api.delete(`/divers/mappings/${id}`);
        mutate();
    };

    return (
        <Card className="ec-card" data-testid="mappings-card">
            <CardContent className="p-6 space-y-4">
                <div>
                    <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                        <MagicWand size={18} weight="duotone" />
                        Mapping des fournisseurs
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Quand l&apos;expéditeur d&apos;un mail contient le motif (ex: <code>orange.fr</code>),
                        la facture sera enregistrée sous le nom de fournisseur correspondant.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                    <div className="space-y-1">
                        <Label htmlFor="map-pattern" className="text-xs">Motif (expéditeur)</Label>
                        <Input
                            id="map-pattern"
                            placeholder="ex: orange.fr"
                            value={pattern}
                            onChange={(e) => setPattern(e.target.value)}
                            data-testid="mapping-pattern"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="map-vendor" className="text-xs">Nom du fournisseur</Label>
                        <Input
                            id="map-vendor"
                            placeholder="Orange"
                            value={vendor}
                            onChange={(e) => setVendor(e.target.value)}
                            data-testid="mapping-vendor"
                        />
                    </div>
                    <Button onClick={add} disabled={saving} className="bg-primary hover:bg-primary/90"
                            data-testid="mapping-add">
                        {saving ? "…" : "Ajouter"}
                    </Button>
                </div>

                {items.length > 0 ? (
                    <div className="border border-border rounded-md divide-y divide-border">
                        {items.map((m) => (
                            <div key={m.id} className="flex items-center justify-between px-3 py-2"
                                 data-testid={`mapping-row-${m.id}`}>
                                <div className="flex items-center gap-2 text-sm">
                                    <code className="text-xs bg-muted px-2 py-0.5 rounded">{m.pattern}</code>
                                    <span className="text-muted-foreground">→</span>
                                    <span className="font-semibold">{m.vendor}</span>
                                </div>
                                <Button
                                    variant="ghost" size="sm"
                                    onClick={() => remove(m.id)}
                                    className="text-destructive hover:bg-destructive/10"
                                    data-testid={`mapping-delete-${m.id}`}
                                >
                                    <Trash size={14} />
                                </Button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-xs text-muted-foreground text-center py-4">
                        Aucun mapping défini. Sans mapping, le nom du fournisseur sera deviné depuis le domaine de l&apos;expéditeur.
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
